import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import 'react-datepicker/dist/react-datepicker.css';
import './MessageLogs.css';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import FilterBar from '../FilterComponents/FilterBar';
import DialogOkCancelFormComponent from '../Modal/DialogOkCancelFormComponent';
import { resendFile } from '../../helpers/fileHelper';
import { getApiKey } from '../../helpers/stringHelpers';
import { useDropdownFilters } from '../../hooks/useDropdownFilters';
import { useLogTableState } from '../../hooks/useLogTableState';
import LogTable from '../LogTableComponents/LogTable';

function UniqErrorMsg({ config }) {
    const { accounts, instance } = useMsal();

    // Use shared custom hook for state management
    const {
        state,
        dispatch,
        ACTIONS,
        openModal,
        closeModal,
        resetModal,
        setModalData,
        handleInputChange,
        handleInputKeyPress,
        handleSearchClick,
        handleDateFromChange,
        handleDateToChange,
        handleMaxRowsChange,
        handleMaxRowsKeyPress,
        handleTransactionTagClick,
        handleClearTransactionTagFilter,
        toggleDatePicker,
        resetFilters,
    } = useLogTableState(100);

    const handleModalOK = () => {
        resendFile(state.modalData, config, accounts, instance);
        closeModal();
    };

    const handleResendFile = (modalData) => {
        setModalData(modalData);
        openModal();
    };

    // Handle dropdown filter changes
    const dropdownDaysAgo = 30;
    const dropdownFields = ["sender", "receiver", "transactionType", "messageType"];

    const {
        dropdownOptions,
        selectedValues,
        handleDropdownChange,
        resetAllFilters
    } = useDropdownFilters(dropdownFields, state.dropdownTable, 'shared_dropdownFilters');

    // Combined reset function for all filters
    const handleResetAllFilters = () => {
        resetFilters(); // Reset date, text, max rows, transaction tag
        resetAllFilters(); // Reset dropdown filters
    };

    const tableHeaders = [
        { title: "#", isDropdown: false, maxWidth: "25px" },
        { title: "Processed", isDropdown: false, maxWidth: "67px" },
        { title: "Sender", isDropdown: true, field: "sender", maxWidth: "80px" },
        { title: "Receiver", isDropdown: true, field: "receiver", maxWidth: "80px" },
        { title: "Transaction Type", isDropdown: true, field: "transactionType", maxWidth: "80px" },
        { title: "Message Type", isDropdown: true, field: "messageType", maxWidth: "70px" },
        { title: "Transaction Tag", isDropdown: false, field:"transactionTag", maxWidth: "70px" },
        { title: "Description", isDropdown: false, maxWidth: "400px" },
        { title: "Status", isDropdown: false, maxWidth: "50px" },
        { title: "FileName", isDropdown: false, maxWidth: "70px" },
        { title: "Scope", isDropdown: false, maxWidth: "40px" },
        { title: "System", isDropdown: false, maxWidth: "40px" },
        { title: "Snapshot", isDropdown: false, maxWidth: "10px" }
    ];

    // Fetch dropdown data on component mount with session caching
    useEffect(() => {
        const cacheKey = `dropdownData_error_${config.enviromentScope}_${dropdownDaysAgo}`;
        const cacheTimeKey = `${cacheKey}_timestamp`;
        
        // Check for cached data (valid for 5 minutes)
        const cachedData = sessionStorage.getItem(cacheKey);
        const cacheTimestamp = sessionStorage.getItem(cacheTimeKey);
        const cacheAge = cacheTimestamp ? Date.now() - parseInt(cacheTimestamp, 10) : null;
        
        // Use cache if it's less than 5 minutes old
        if (cachedData && cacheAge && cacheAge < 300000) {
            try {
                dispatch({ type: ACTIONS.SET_DROPDOWN_TABLE, payload: JSON.parse(cachedData) });
                dispatch({ type: ACTIONS.SET_LOADING_DROPDOWN_OPTIONS, payload: false });
                return;
            } catch (error) {
                console.warn('Failed to parse cached dropdown data', error);
                // Continue to fetch fresh data if cache is corrupted
            }
        }

        dispatch({ type: ACTIONS.SET_LOADING_DROPDOWN_OPTIONS, payload: true });
        const applicationId = config.clientId;

        const accessTokenRequest = {
            scopes: ["api://" + applicationId + "/User.Read"],
            account: accounts[0],
        };

        instance
            .acquireTokenSilent(accessTokenRequest)
            .then((accessTokenResponse) => {
                const accessToken = accessTokenResponse.accessToken;
                const logmsgApiUrl = config.logMsgApiUrl;
                const apiKey = getApiKey(config.enviromentScope);

                const getRequest = `${logmsgApiUrl}/LogsGetGroup?days=${dropdownDaysAgo}&status=error&type=7`;

                const headers = {
                    Authorization: `Bearer ${accessToken}`,
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    APIKEY: apiKey
                };

                axios.get(getRequest, { headers })
                    .then((response) => {
                        dispatch({ type: ACTIONS.SET_DROPDOWN_TABLE, payload: response.data });
                        // Cache the raw data (not the computed options!)
                        try {
                            sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
                            sessionStorage.setItem(cacheTimeKey, Date.now().toString());
                        } catch (error) {
                            console.warn('Failed to cache dropdown data', error);
                            // Continue even if caching fails (e.g., quota exceeded)
                        }
                    })
                    .catch((error) => console.error(error))
                    .finally(() => dispatch({ type: ACTIONS.SET_LOADING_DROPDOWN_OPTIONS, payload: false }));
            })
            .catch((error) => console.error(error));
    }, [config.clientId, config.enviromentScope, config.logMsgApiUrl, accounts, instance]);

    // Find all log messages from API, based on filters: word, dates and dropdown.
    // If any of these filter changes this useEffect should run
    useEffect(() => {
        dispatch({ type: ACTIONS.SET_LOADING, payload: true }); // Set loading to true when starting to fetch data

        const applicationId = config.clientId;

        const accessTokenRequest = {
            scopes: ["api://" + applicationId + "/User.Read"],
            account: accounts[0],
        };
        const fetchLogMsgTableData = () => {
            instance
                .acquireTokenSilent(accessTokenRequest)
                .then((accessTokenResponse) => {
                    // Acquire token silent success
                    let accessToken = accessTokenResponse.accessToken;
                    // Call your API with token
                    // Get config values

                    const logmsgApiUrl = config.logMsgApiUrl;
                    const apiKey = getApiKey(config.enviromentScope);

                    // Setup GET query
                    let getRequest = logmsgApiUrl + '/LogsGetAllNoBodyLastIdError?';

                    // Setup Header
                    const headers = {
                        'Authorization': 'Bearer ' + accessToken,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'APIKEY': apiKey
                    };

                    // Update Params based on state of the filters
                    const updateQueryParams = () => {
                        const APIKEY = apiKey;
                        const dtFrom = state.dateFrom.toISOString().split('T')[0];
                        const dtTo = state.dateTo.toISOString().split('T')[0];
                        const filter = state.filterWords;

                        // Dynamically add dropdown fields to query parameters
                        const dropdownParams = dropdownFields.reduce((params, field) => {
                            params[field] = selectedValues[field] !== "All" ? selectedValues[field] : null;
                            return params;
                        }, {});

                        // Add transactionTag filter if set
                        if (state.transactionTagFilter) {
                            dropdownParams.transactionTag = state.transactionTagFilter;
                        }

                        return { APIKEY, dtFrom, dtTo, maxRows: state.maxRows, filter, ...dropdownParams };
                    };
                    const queryParams = updateQueryParams();

                    // Make the API request
                    axios.get(getRequest, {
                        params: queryParams,
                        headers: headers
                    })
                        .then((response) => {
                            // Set the fetched data to the logMessageTable variable
                            if (response.data === "" || response.data === null) {
                                dispatch({ type: ACTIONS.SET_LOG_MESSAGE_TABLE, payload: [] });
                                dispatch({ type: ACTIONS.SET_TOTAL_ROWS, payload: 0 });
                            } else {
                                dispatch({ type: ACTIONS.SET_LOG_MESSAGE_TABLE, payload: response.data });
                                dispatch({ type: ACTIONS.SET_TOTAL_ROWS, payload: response.data.length });
                                dispatch({ type: ACTIONS.SET_LATEST_UPDATE, payload: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }) });
                            }
                        })
                        .catch((error) => {
                            // Handle errors
                            console.log(error);
                        })
                        .finally(() => {
                            dispatch({ type: ACTIONS.SET_LOADING, payload: false }); // Set loading to false when data fetching is completed (success or failure)
                        });
                })
                .catch((error) => {
                    console.log(error);
                });
        };
        // Fetch immediately on mount
        fetchLogMsgTableData();

        // Set interval to refresh every 5 minutes (300000 ms)
        const interval = setInterval(fetchLogMsgTableData, 300000);

        // Cleanup interval on component unmount
        return () => clearInterval(interval);
    }, [state.dateFrom, state.dateTo, state.filterWords, selectedValues, state.inputEnterKey, state.maxRows, state.maxRowsInputEnterKey, state.transactionTagFilter, config.clientId, config.logMsgApiUrl, config.enviromentScope, accounts, instance]); // Use dependency array to run the effect only when at least one of these values are changed

    return (
        <div className="message-logs-container">
            <h2 className="message-logs-title">Uniq Error Logs</h2>

            {/* Filter inputs in a horizontal line */}
            <FilterBar
                config={config}
                inputValue={state.inputValue}
                handleInputChange={handleInputChange}
                handleInputKeyPress={handleInputKeyPress}
                handleSearchClick={handleSearchClick}
                dateFrom={state.dateFrom}
                handleDateFromChange={handleDateFromChange}
                toggleDatePicker={toggleDatePicker}
                dateTo={state.dateTo}
                handleDateToChange={handleDateToChange}
                maxRowsInputValue={state.maxRowsInputValue}
                handleMaxRowsChange={handleMaxRowsChange}
                handleMaxRowsKeyPress={handleMaxRowsKeyPress}
                totalRows={state.totalRows}
                latestUpdate={state.latestUpdate}
                onResetFilters={handleResetAllFilters}
            />

            <LogTable
                loading={state.loading}
                userconfig={"_UniqErrorMsg_" + (accounts[0]?.username || 'Unknown User')}
                tableHeaders={tableHeaders}
                selectedValues={selectedValues}
                dropdownOptions={dropdownOptions}
                handleDropdownChange={handleDropdownChange}
                loadingDropdownOptions={state.loadingDropdownOptions}
                logMessageTable={state.logMessageTable}
                config={config}
                modalOpen={state.modalOpen}
                openModal={openModal}
                closeModal={closeModal}
                handleResendFile={handleResendFile}
                accounts={accounts}
                instance={instance}
                onTransactionTagClick={handleTransactionTagClick}
                transactionTagFilter={state.transactionTagFilter}
                handleClearTransactionTagFilter={handleClearTransactionTagFilter}
            />

            <DialogOkCancelFormComponent
                isOpen={state.modalOpen}
                onRequestClose={resetModal}
                TextTitle={state.modalData.TextTitle}
                TextBody1={state.modalData.TextBody1}
                TextBody2={state.modalData.TextBody2}
                TextBody3={state.modalData.TextBody3}
                TextBody4={state.modalData.TextBody4}
                TextBody5={state.modalData.TextBody5}
                onResult={handleModalOK}
                onClose={resetModal}
            />
        </div>
    );
}

UniqErrorMsg.propTypes = {
    config: PropTypes.shape({
        clientId: PropTypes.string.isRequired,
        logMsgApiUrl: PropTypes.string.isRequired,
        color: PropTypes.string.isRequired,
        enviromentScope: PropTypes.string.isRequired,
    }).isRequired,
};

export default UniqErrorMsg;
